/**
 * Quem declara ter trabalho em andamento que um recarregamento destruiria.
 *
 * O recarregamento automático depois de um deploy é bom para todo mundo, menos
 * para quem está no meio de uma frase. O campo de mensagem do atendimento e os
 * formulários longos avisam aqui, e a decisão de recarregar respeita.
 *
 * Registro global e não um contexto do React de propósito: quem precisa avisar
 * está espalhado pela aplicação, e obrigar cada tela a estar dentro de um
 * provedor só para isto convidaria a esquecer.
 */

type UnsavedWorkProbe = () => boolean

const probes = new Map<string, UnsavedWorkProbe>()

/**
 * Registra uma sonda. Devolve a função que a remove — use no `useEffect`.
 *
 * `id` estável por tela/campo: registrar duas vezes o mesmo id substitui, o que
 * é o certo quando o componente remonta.
 */
export function registerUnsavedWork(id: string, probe: UnsavedWorkProbe): () => void {
    probes.set(id, probe)
    return () => {
        // Só remove se ainda for a MESMA sonda: numa remontagem o efeito novo
        // registra antes de o antigo limpar, e sem esta guarda a limpeza do
        // antigo apagaria o registro do novo.
        if (probes.get(id) === probe) probes.delete(id)
    }
}

/** Alguém tem algo a perder agora? Sonda que explode conta como "sim". */
export function hasUnsavedWork(): boolean {
    for (const probe of probes.values()) {
        try {
            if (probe()) return true
        } catch {
            // Na dúvida, protege o usuário: avisa em vez de recarregar por cima.
            return true
        }
    }
    return false
}
